import { format, isValid, parse, parseISO } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { roundToCents } from "@common/helpers/monetary";
import { formatDateOnly } from "@helpers/date";
import { asFiniteNumber, toDisplayText } from "../cells/cellValues";
import { asDateOnly } from "../cells/dateOnlyValue";
import type {
    DataGridColumnKind,
    DataGridEditor,
    DataGridEditorKind,
    DataGridEditorOption,
} from "../columns/types";
import type { DataGridCellValue } from "../types";

export type ParseResult =
    | { ok: true; value: DataGridCellValue }
    | { ok: false; message: string };

export const getEditorKind = (editor: DataGridEditor): DataGridEditorKind =>
    typeof editor === "string" ? editor : editor.kind;

const getOptions = (editor: DataGridEditor) =>
    typeof editor === "string" ? [] : editor.options;

/** `yyyy` reads 1 to 4 digits, so shorter years are rejected here. */
const MIN_YEAR = 1000;
// `M/d/yy` goes first: `yyyy` would also read "26" as the year 26.
const DATE_FORMATS = [
    "yyyy-MM-dd",
    "M/d/yy",
    "M/d/yyyy",
    fullDateFullYearFormat,
    "MMM d yyyy",
    "MMMM d, yyyy",
];
const YEARLESS_DATE_FORMATS = ["M/d", "MMM d", "MMMM d"];
/** Sets the century for two-digit years when no workspace day is given. */
const TWO_DIGIT_YEAR_REFERENCE = new Date(2000, 0, 1);

const parseDate = (text: string, today: string | undefined): string | null => {
    const reference =
        today === undefined ? TWO_DIGIT_YEAR_REFERENCE : parseISO(today);
    const patterns =
        today === undefined
            ? DATE_FORMATS
            : [...DATE_FORMATS, ...YEARLESS_DATE_FORMATS];
    for (const pattern of patterns) {
        const date = parse(text, pattern, reference);
        if (isValid(date) && date.getFullYear() >= MIN_YEAR) {
            return format(date, "yyyy-MM-dd");
        }
    }
    return null;
};

/**
 * An unsigned number: thousands commas only in groups of three after a first
 * group that doesn't start with 0; a leading or trailing point is fine.
 */
const UNSIGNED_NUMBER_PATTERN = /^(?:[1-9]\d{0,2}(?:,\d{3})+|\d+)?(?:\.\d*)?$/;

/**
 * Accepts `1,234.5`, `$1,234.50`, `-$12`, `5.`, and accounting negatives
 * `(12.00)` and `$(1,234.50)`. Commas must group thousands, so `12,50` and
 * `0,125` are rejected, not read as 1250 and 125. Spaces may surround the
 * number and `$` may come before its first digit, but neither may sit inside
 * it: `12 50` and `1$2` are rejected.
 */
const parseNumber = (text: string): number | null => {
    if (/[\d.,]\s+[\d.,]/.test(text) || /\d.*\$/.test(text)) {
        return null;
    }
    const compact = text.replace(/\s/g, "").replace("$", "");
    const isParenthesized = /^\(.*\)$/.test(compact);
    const inner = isParenthesized ? compact.slice(1, -1) : compact;
    const unsigned = inner.replace(/^[-+]/, "");
    if (
        !/\d/.test(unsigned) ||
        !UNSIGNED_NUMBER_PATTERN.test(unsigned) ||
        (isParenthesized && unsigned !== inner)
    ) {
        return null;
    }
    const value = Number(inner.replaceAll(",", ""));
    if (!Number.isFinite(value)) {
        return null;
    }
    return isParenthesized ? -value : value;
};

/** `String(value)` without exponent notation (`1e-7`, `1e+21`), which the parser rejects. */
const toPlainDecimal = (value: number): string => {
    const text = String(value);
    const match = /^(-?)(\d)(?:\.(\d+))?e([+-]\d+)$/.exec(text);
    if (!match) {
        return text;
    }
    const [, sign, lead, fraction = "", exponentText] = match;
    const digits = `${lead}${fraction}`;
    const exponent = Number(exponentText);
    return exponent < 0
        ? `${sign}0.${"0".repeat(-exponent - 1)}${digits}`
        : `${sign}${digits}${"0".repeat(exponent + 1 - digits.length)}`;
};

const findOptionByValue = (
    options: readonly DataGridEditorOption[],
    text: string,
): DataGridEditorOption | undefined =>
    options.find((option) => option.value === text);

const findOptionByLabel = (
    options: readonly DataGridEditorOption[],
    text: string,
): DataGridEditorOption | undefined => {
    const query = text.trim().toLowerCase();
    return options.find((option) => option.label.toLowerCase() === query);
};

/**
 * Turns typed or pasted text into the column's value; blank text clears the
 * cell. A date without a year (`Sep 20`, `9/20`) lands in the year of
 * `today`, the workspace day (`YYYY-MM-DD`), and doesn't parse without it.
 */
export const parseEditorText = ({
    editor,
    text,
    today,
}: {
    editor: DataGridEditor;
    text: string;
    today?: string;
}): ParseResult => {
    const trimmed = text.trim();
    if (trimmed === "") {
        return { ok: true, value: null };
    }
    switch (getEditorKind(editor)) {
        case "text":
            return { ok: true, value: trimmed };
        case "number": {
            const value = parseNumber(trimmed);
            return value === null
                ? { ok: false, message: "Enter a number" }
                : { ok: true, value };
        }
        case "money": {
            const value = parseNumber(trimmed);
            return value === null
                ? { ok: false, message: "Enter an amount" }
                : { ok: true, value: roundToCents(value) };
        }
        case "date": {
            const value = parseDate(trimmed, today);
            return value === null
                ? { ok: false, message: "Enter a date like Sep 20, 2026" }
                : { ok: true, value };
        }
        case "picker": {
            const options = getOptions(editor);
            const option =
                findOptionByLabel(options, trimmed) ??
                findOptionByValue(options, trimmed);
            return option
                ? { ok: true, value: option.value }
                : { ok: false, message: `No option named "${trimmed}"` };
        }
    }
};

/** A cell's current value as its editor commits it (the change's `previousValue`). */
export const toEditorValue = <TValue>({
    editor,
    value,
}: {
    editor: DataGridEditor;
    value: TValue;
}): DataGridCellValue => {
    switch (getEditorKind(editor)) {
        case "text":
            return toDisplayText(value);
        case "number":
        case "money":
            return asFiniteNumber(value);
        case "date":
            return asDateOnly(value);
        case "picker": {
            const text =
                typeof value === "string" || typeof value === "number"
                    ? String(value)
                    : (toDisplayText(value) ?? "");
            const options = getOptions(editor);
            return (
                (
                    findOptionByValue(options, text) ??
                    findOptionByLabel(options, text)
                )?.value ?? null
            );
        }
    }
};

/** The text an editor opens with when editing in place (F2, Enter, double-click). */
export const toEditorText = ({
    editor,
    value,
}: {
    editor: DataGridEditor;
    value: DataGridCellValue;
}): string => {
    if (value === null) {
        return "";
    }
    const kind = getEditorKind(editor);
    if (kind === "money" && typeof value === "number") {
        return value.toFixed(2);
    }
    if (kind === "date" && typeof value === "string") {
        return formatDateOnly(value, fullDateFullYearFormat);
    }
    if (kind === "picker") {
        return (
            getOptions(editor).find((option) => option.value === value)
                ?.label ?? ""
        );
    }
    return typeof value === "number" ? toPlainDecimal(value) : String(value);
};

/**
 * Plain text for copy: raw numbers and calendar days, and a picker's option
 * label, so a paste parses back.
 */
export const toCopyText = <TValue>({
    kind,
    editor,
    value,
}: {
    kind: DataGridColumnKind;
    editor: DataGridEditor | undefined;
    value: TValue;
}): string => {
    const optionValue =
        editor && getEditorKind(editor) === "picker"
            ? toEditorValue({ editor, value })
            : null;
    if (editor && optionValue !== null) {
        return toEditorText({ editor, value: optionValue });
    }
    if (kind === "date") {
        return asDateOnly(value) ?? "";
    }
    if (kind === "number" || kind === "money") {
        const number = asFiniteNumber(value);
        return number === null ? "" : toPlainDecimal(number);
    }
    return toDisplayText(value) ?? "";
};
